import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import canAuthenticateWithPassword from '../../account/canAuthenticateWithPassword';
import { SessionActions } from '../../account/sessionSlice';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated/gql';
import useSSOPlatforms from '../../integrations/useSSOPlatforms';
import useIsPasswordSet from '../../onboarding/useIsPasswordSet';
import UpgradeBadge from '../../organization/UpgradeBadge';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import { userToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import { isFeatureEnabled } from '../../utils/features';
import Link from '../../utils/router/Link';
import { openPricingModal } from '../../utils/url/pricingPath';
import useUpdateEmailMutation from '../useUpdateEmailMutation';
import useUpdatePasswordMutation from '../useUpdatePasswordMutation';
import * as styles from './UserAccountSettings.module.scss';
import UserThemeSetting from './UserThemeSetting';

export const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_REQUIREMENT_HELP_TEXT = `Choose a password at least ${PASSWORD_MIN_LENGTH} characters in length.`;

export const validatePassword = (password: string) => {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      password: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
    };
  }

  return undefined;
};

gql(`
  fragment UserAccountSettings_userSettings on UserSettings {
    allowAccountAccess
    aiAutofix
    aiPredict
    aiAsk
    preferDesktop
    uiTheme
  }
`);

const USER_FRAGMENT = gql(`
  fragment UserAccountSettings_user on User {
    id
    settings {
      ...UserAccountSettings_userSettings
    }
  }
`);

const UPDATE_USER_ACCOUNT_SETTINGS_MUTATION = gql(/** @complexity 2 */ `
  mutation updateUserAccountSettings(
    $allowAccountAccess: Boolean
    $aiAutofix: Boolean
    $aiPredict: Boolean
    $aiAsk: Boolean
    $preferDesktop: Boolean
    $uiTheme: UiThemeSetting
  ) {
    updateUserSettings(allowAccountAccess: $allowAccountAccess, aiAutofix: $aiAutofix, aiPredict: $aiPredict, aiAsk: $aiAsk, preferDesktop: $preferDesktop, uiTheme: $uiTheme) {
      ...UserAccountSettings_userSettings
    }
  }
`);

const USER_ACCOUNT_SETTINGS_QUERY = gql(/** @complexity 5 */ `
  query UserAccountSettings {
    session: currentSession {
      organization {
        id
        ...canAuthenticateWithPassword_organization
      }
      user {
        id
        email
        ...UserAccountSettings_user
      }
    }
  }
`);

const ToggleForm = ({
  value,
  onUpdate,
  disabled,
}: {
  value: boolean;
  onUpdate: (value: boolean) => void;
  disabled?: boolean;
}) => (
  <Form enableReinitialize initialValues={{ field: !disabled && value }}>
    <Field
      name="field"
      disabled={disabled}
      onChange={(e: React.ChangeEvent) => {
        if ('checked' in e.target) {
          onUpdate(Boolean(e.target.checked));
        }
      }}
      toggle
      type="checkbox"
    />
  </Form>
);

type EmailFormValues = {
  email?: string | null;
};

type UpdatePasswordFormValues = {
  oldPassword: string;
  password: string;
};

const UserAccountSettings = () => {
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const { id, type: userType } = useAppSelector((state) => state.session.user);

  const { data: { session } = {} } = useQuery(USER_ACCOUNT_SETTINGS_QUERY);
  const ssoPlatforms = useSSOPlatforms() || [];
  const { organization, user } = session ?? {};

  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const emailChanged = searchParams.get('emailChanged');

    if (emailChanged) {
      const hasEmailChanged = emailChanged === '1';

      dispatch(
        UIActions.pushToast(
          hasEmailChanged
            ? userToasts.emailUpdated
            : userToasts.emailUpdateFailedError,
          { duration: 7000 },
        ),
      );

      setSearchParams({}, { replace: true });
    }
  }, [dispatch, setSearchParams, searchParams]);

  const { data: isPasswordSet } = useIsPasswordSet();
  const updateUserEmail = useUpdateEmailMutation();
  const updateUserPassword = useUpdatePasswordMutation();
  const passwordText = isPasswordSet ? '********' : '';

  const handleUpdateEmail = (values: EmailFormValues) => {
    if (!values.email) return;

    updateUserEmail(id, values.email).then(() => {
      setIsEditingEmail(false);
      if (values.email !== user?.email) {
        dispatch(
          UIActions.pushToast(userToasts.verifyYourEmailWarning, {
            duration: 7000,
          }),
        );
      }
    });
  };

  const [updateUserAccountSettings] = useMutation(
    UPDATE_USER_ACCOUNT_SETTINGS_MUTATION,
    {
      update: (cache, { data }) => {
        if (data?.updateUserSettings) {
          cache.writeFragment({
            fragment: USER_FRAGMENT,
            fragmentName: 'UserAccountSettings_user',
            id: cache.identify({
              __typename: 'User',
              id,
            }),
            data: {
              __typename: 'User',
              id,
              settings: {
                ...user?.settings,
                ...data.updateUserSettings,
              },
            },
          });
        }
      },
    },
  );

  const handleUpdatePassword = (values: UpdatePasswordFormValues) =>
    updateUserPassword(values).then(() => setIsEditingPassword(false));

  const handleUpdate = async <
    Key extends keyof VariablesOf<typeof UPDATE_USER_ACCOUNT_SETTINGS_MUTATION>,
  >(
    key: Key,
    value: VariablesOf<typeof UPDATE_USER_ACCOUNT_SETTINGS_MUTATION>[Key],
    options?: { syncWithRedux: boolean },
  ) => {
    await updateUserAccountSettings({
      variables: { [key]: value },
      optimisticResponse: {
        updateUserSettings: { ...user?.settings, [key]: value },
      },
    });

    if (options?.syncWithRedux) {
      dispatch(SessionActions.setUserSetting({ [key]: value }));
    }
  };

  return (
    <SettingsContainerView title="Account">
      <SettingsSection header="Authentication">
        <Cards>
          {isEditingEmail ? (
            <SettingCard
              description={
                <Form<EmailFormValues>
                  onSubmit={handleUpdateEmail}
                  initialValues={{ email: user?.email }}
                  enableReinitialize
                  apiErrorMessages={{
                    DUPLICATE_EMAIL: 'Email already in use by another user',
                  }}
                >
                  {({ values }) => (
                    <div className={styles.fieldContainer}>
                      <Field
                        className={styles.field}
                        id="userEmail"
                        type="email"
                        name="email"
                        autoComplete="email"
                        margin="none"
                        autoFocus
                        required
                      />
                      <div>
                        <Submit
                          disabled={
                            !values.email || values.email === user?.email
                          }
                          variant="primary"
                        >
                          Save
                        </Submit>
                      </div>
                    </div>
                  )}
                </Form>
              }
              title={<label htmlFor="userEmail">Email address</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingEmail(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Email address"
              description={user?.email}
            />
          )}
          {organization &&
            canAuthenticateWithPassword(
              organization,
              ssoPlatforms,
              userType ?? undefined,
            ) &&
            (isEditingPassword ? (
              <SettingCard
                testId="passwordSettingCard"
                description={
                  <Form<UpdatePasswordFormValues>
                    onSubmit={handleUpdatePassword}
                    validate={(values) => validatePassword(values.password)}
                    initialValues={{ oldPassword: '', password: '' }}
                    apiErrorMessages={{
                      FORBIDDEN: 'Current password is incorrect',
                    }}
                  >
                    {({ isValid, dirty }) => (
                      <>
                        <input
                          className={styles.hide}
                          name="email"
                          type="email"
                          value={user?.email ?? undefined}
                          autoComplete="username"
                          readOnly
                          aria-label="Email address"
                        />
                        {isPasswordSet && (
                          <Field
                            type="password"
                            name="oldPassword"
                            label="Current password"
                            autoComplete="current-password"
                            margin="small"
                            autoFocus
                            required
                          />
                        )}
                        <Field
                          type="password"
                          name="password"
                          label="New password"
                          autoComplete="new-password"
                          helpText={`${
                            isPasswordSet
                              ? ''
                              : 'You’ve never picked a password. '
                          }
                              ${PASSWORD_REQUIREMENT_HELP_TEXT}`}
                          margin="small"
                          autoFocus={!isPasswordSet}
                          required
                        />
                        <div className={styles.submitContainer}>
                          <Submit
                            disabled={!isValid || !dirty}
                            variant="primary"
                          >
                            Save
                          </Submit>
                        </div>
                      </>
                    )}
                  </Form>
                }
              />
            ) : (
              <SettingCard
                testId="passwordSettingCard"
                actions={
                  <Button
                    onClick={() => setIsEditingPassword(true)}
                    variant="secondary"
                    disabled={isPasswordSet === undefined}
                  >
                    Edit
                  </Button>
                }
                title="Password"
                description={
                  isPasswordSet === undefined ? (
                    <LoadingIndicator />
                  ) : (
                    passwordText
                  )
                }
              />
            ))}
        </Cards>
      </SettingsSection>
      <SettingsSection header="Personalization">
        <Cards>
          <SettingCard
            title="AI Autofix"
            description="Automatically catch and fix typos when editing posts"
            actions={
              <ToggleForm
                value={!!user?.settings?.aiAutofix}
                onUpdate={(value) =>
                  handleUpdate('aiAutofix', value, { syncWithRedux: true })
                }
              />
            }
          />

          <SettingCard
            title={
              <>
                AI Predict
                {!isFeatureEnabled('ai_predict') && (
                  <span className={styles.badge}>
                    <UpgradeBadge
                      onClick={() => {
                        openPricingModal(dispatch, 'ai_predict');
                      }}
                    />
                  </span>
                )}
              </>
            }
            description="Predict and suggest your next words when editing posts"
            actions={
              <ToggleForm
                disabled={!isFeatureEnabled('ai_predict')}
                value={!!user?.settings?.aiPredict}
                onUpdate={(value) => handleUpdate('aiPredict', value)}
              />
            }
          />
          <SettingCard
            title={
              <>
                AI Copilot
                {!isFeatureEnabled('ai_ask') && (
                  <span className={styles.badge}>
                    <UpgradeBadge
                      onClick={() => {
                        openPricingModal(dispatch, 'ai_ask');
                      }}
                    />
                  </span>
                )}
              </>
            }
            description="Get answers using accessible published posts"
            actions={
              <ToggleForm
                disabled={!isFeatureEnabled('ai_ask')}
                value={!!user?.settings?.aiAsk}
                onUpdate={(value) =>
                  handleUpdate('aiAsk', value, { syncWithRedux: true })
                }
              />
            }
          />
          <UserThemeSetting
            uiTheme={user?.settings?.uiTheme ?? undefined}
            onUpdateUiTheme={(value) =>
              handleUpdate('uiTheme', value, { syncWithRedux: true })
            }
          />
          <SettingCard
            title="Open in desktop app"
            description={
              <>
                Show option on post pages to view in the desktop app.{' '}
                <Link to="https://slab.com/downloads" newTab>
                  Download app
                </Link>
              </>
            }
            actions={
              <ToggleForm
                value={!!user?.settings?.preferDesktop}
                onUpdate={(value) =>
                  handleUpdate('preferDesktop', value, { syncWithRedux: true })
                }
              />
            }
          />
        </Cards>
      </SettingsSection>
      <SettingsSection header="Support">
        <Cards>
          <SettingCard
            title="Give Slab access"
            description="Allow Slab’s support staff to access your account to diagnose and resolve issues you’ve raised."
            actions={
              <ToggleForm
                value={!!user?.settings?.allowAccountAccess}
                onUpdate={(value) =>
                  handleUpdate('allowAccountAccess', value, {
                    syncWithRedux: true,
                  })
                }
              />
            }
          />
        </Cards>
      </SettingsSection>
    </SettingsContainerView>
  );
};

export default UserAccountSettings;
